import { Copy, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigationType, useParams } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert';
import Modal from '../components/Modal';
import ExerciseVideoSheet from '../components/plans/ExerciseVideoSheet';
import { isUploadLimitError, uploadErrorText } from '../components/plans/exercise-video-form';
import ShareButton from '../components/ShareButton';
import { TRAINING_PLANS_SECTION_ID, SECTION_QUERY_KEY } from '../lib/scroll-intent';
import { saveBookingFormDraft, type PlanFormReturnState } from '../lib/form-return';
import {
  claimAbandonedPlanFormDraft,
  planFormDraftStorageKey,
  savePlanFormDraftForVideoLimit,
  type PlanFormExerciseDraft,
} from '../lib/plan-form-draft';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import { useLeaveUploadWarning } from '../hooks/useLeaveUploadWarning';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { readDurationSec } from '../lib/compress-video';
import { uploadExerciseVideo } from '../lib/upload-exercise-video';
import { formatTrainingPlanText } from '../lib/training-plan-share';
import {
  filterClientPlans,
  sortPlansByRecency,
} from '../lib/training-plan-utils';
import {
  type Exercise,
  type MovementMatch,
  useCreateTrainingPlanMutation,
  useGeneratePlanMutation,
  useGetClientQuery,
  useGetTrainingPlanQuery,
  useGetTrainingPlansQuery,
  useLazyGetTrainingPlanQuery,
  useDeleteVideoMutation,
  useLazyPlaybackQuery,
  useMatchMovementsMutation,
  useSyncVideosFromDiskMutation,
  useUpdateTrainingPlanMutation,
} from '../store/coachApi';

type ExerciseRow = PlanFormExerciseDraft;

type PlaybackState = {
  href?: string;
  error?: string;
  loading?: boolean;
};

type MatchChoice = {
  rowKey: string;
  name: string;
  checked: boolean;
  movement: MovementMatch['movement'];
};

function toRow(ex: Exercise, options?: { copy?: boolean }): ExerciseRow {
  const videoId = ex.videoId ?? null;
  const row: ExerciseRow = {
    rowKey: crypto.randomUUID(),
    name: ex.name,
    sets: ex.sets,
    reps: ex.reps,
    restSeconds: ex.restSeconds || '',
    notes: ex.notes || '',
    movementId: ex.movementId ?? null,
    videoId,
    videoSource: videoId ? 'custom' : 'none',
    uploadState: 'idle',
  };
  if (!options?.copy && ex.id) {
    row.id = ex.id;
  }
  return row;
}

function toPayload(rows: ExerciseRow[]): Exercise[] {
  return rows
    .filter((ex) => ex.name.trim())
    .map((ex) => ({
      ...(ex.id ? { id: ex.id } : {}),
      name: ex.name.trim(),
      sets: ex.sets.trim() || '0',
      reps: ex.reps.trim() || '0',
      restSeconds: ex.restSeconds.trim() || undefined,
      notes: ex.notes.trim() || undefined,
      movementId: ex.movementId ?? null,
      videoId: ex.videoId ?? null,
    }));
}

function videoSourceLabel(
  source: ExerciseRow['videoSource'],
  t: (key: string) => string,
): string {
  if (source === 'library') return t('Ролик из библиотеки');
  if (source === 'custom') return t('Свой ролик');
  return t('Без ролика');
}

function emptyExercise(): ExerciseRow {
  return {
    rowKey: crypto.randomUUID(),
    name: '',
    sets: '',
    reps: '',
    restSeconds: '',
    notes: '',
    movementId: null,
    videoId: null,
    videoSource: 'none',
    uploadState: 'idle',
  };
}

function AutoHeightTextarea({
  value,
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      {...props}
      ref={ref}
      rows={1}
      value={value}
      className={`resize-none overflow-hidden ${className ?? ''}`}
    />
  );
}

const TrainingPlanFormRoute = () => {
  const navigate = useTransitionNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t } = useTranslation();
  const { id: clientId, planId } = useParams();
  const isEdit = !!planId;
  const returnContext = location.state as PlanFormReturnState | null;
  const [abandonedDraft] = useState(() =>
    clientId
      ? claimAbandonedPlanFormDraft({
          key: planFormDraftStorageKey(clientId, planId),
          clientId,
          navigationType,
          locationState: location.state as { planFormDraftKey?: string } | null,
        })
      : null,
  );

  const { data: client } = useGetClientQuery({ id: clientId! }, { skip: !clientId });
  const { data: allPlans = [] } = useGetTrainingPlansQuery(undefined, { skip: isEdit });
  const [fetchSourcePlan, { isFetching: copyingPlan }] = useLazyGetTrainingPlanQuery();
  const { data: plan, isLoading, error: planError } = useGetTrainingPlanQuery(planId!, {
    skip: !planId,
  });
  const [createTrainingPlan, { isLoading: isCreating }] = useCreateTrainingPlanMutation();
  const [updateTrainingPlan, { isLoading: isUpdating }] = useUpdateTrainingPlanMutation();
  const [generatePlan, { isLoading: isGenerating }] = useGeneratePlanMutation();
  const [matchMovements] = useMatchMovementsMutation();
  const [fetchPlayback] = useLazyPlaybackQuery();
  const [deleteVideo] = useDeleteVideoMutation();
  const [syncVideosFromDisk] = useSyncVideosFromDiskMutation();

  useEffect(() => {
    void syncVideosFromDisk();
  }, [syncVideosFromDisk]);

  const [error, setError] = useState('');
  const [name, setName] = useState(() => abandonedDraft?.name ?? '');
  const [goal, setGoal] = useState('');
  const [copyFromPlanId, setCopyFromPlanId] = useState('');
  const [exercises, setExercises] = useState<ExerciseRow[]>(() => abandonedDraft?.exercises ?? []);
  useLeaveUploadWarning(exercises.some((row) => row.uploadState === 'uploading'));
  const nameRef = useRef(name);
  nameRef.current = name;
  const exercisesRef = useRef(exercises);
  exercisesRef.current = exercises;
  const [videoRowKey, setVideoRowKey] = useState<string | null>(null);
  const [pendingVideoDelete, setPendingVideoDelete] = useState<string | null>(null);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [playbackByRow, setPlaybackByRow] = useState<Record<string, PlaybackState>>({});
  const [savePrompt, setSavePrompt] = useState(false);
  const [linkingVideos, setLinkingVideos] = useState(false);
  const [matchChoices, setMatchChoices] = useState<MatchChoice[] | null>(null);
  const [saveAfterUpload, setSaveAfterUpload] = useState(false);
  const savingAfterRef = useRef(false);
  const persistLock = useRef(false);
  const handleSaveRef = useRef<(rows: ExerciseRow[]) => Promise<void>>(async () => {});

  const copySourcePlans = useMemo(() => {
    if (!clientId) return [];
    return sortPlansByRecency(
      filterClientPlans(allPlans, clientId, 'ACTIVE').filter(
        (plan) => plan.id !== planId,
      ),
    );
  }, [allPlans, clientId, planId]);

  const isArchivedPlan = plan?.status === 'ARCHIVED';

  const hasFormContent = () =>
    name.trim() !== '' || exercises.some((ex) => ex.name.trim() !== '');

  const handleCopyFromPlan = async (sourcePlanId: string) => {
    if (!sourcePlanId) return;

    if (hasFormContent()) {
      const confirmed = window.confirm(t('Текущие данные формы будут заменены. Продолжить?'));
      if (!confirmed) {
        setCopyFromPlanId('');
        return;
      }
    }

    setError('');
    try {
      const source = await fetchSourcePlan(sourcePlanId).unwrap();
      const sourceExercises = source.exercises ?? [];
      setName(`${source.name} (${t('копия')})`);
      setExercises(sourceExercises.map((ex) => toRow(ex, { copy: true })));
      setPlaybackByRow({});
      setMatchChoices(null);
    } catch (err) {
      setCopyFromPlanId('');
      setError(getApiErrorMessage(err, 'Не удалось скопировать план'));
    }
  };

  useEffect(() => {
    if (abandonedDraft) return;

    setError('');
    setGoal('');

    if (!planId) {
      setName('');
      setCopyFromPlanId('');
      setExercises([]);
      setPlaybackByRow({});
      setMatchChoices(null);
      return;
    }

    if (!plan) return;

    const planExercises = plan.exercises ?? [];
    setName(plan.name);
    setExercises(planExercises.map((ex) => toRow(ex)));
    setPlaybackByRow({});
    setMatchChoices(null);
  }, [abandonedDraft, planId, plan]);

  const appliedCopyRef = useRef(false);

  useEffect(() => {
    if (abandonedDraft) return;
    const copyFromPlanId = returnContext?.copyFromPlanId;
    if (isEdit || !copyFromPlanId || appliedCopyRef.current) return;
    appliedCopyRef.current = true;
    void handleCopyFromPlan(copyFromPlanId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, returnContext?.copyFromPlanId]);

  useEffect(() => {
    if (planError) {
      setError(getApiErrorMessage(planError, 'Не удалось загрузить план'));
    }
  }, [planError]);

  const returnToPlans = () => {
    navigate(`/clients/${clientId}?tab=plan&${SECTION_QUERY_KEY}=${TRAINING_PLANS_SECTION_ID}`, {
      replace: true,
    });
  };

  const close = () => {
    if (isEdit) {
      navigate(-1);
      return;
    }
    if (returnContext?.returnTo) {
      navigate(-1);
      return;
    }
    returnToPlans();
  };

  const updateExercise = (
    rowKey: string,
    field: 'name' | 'sets' | 'reps' | 'restSeconds' | 'notes',
    value: string,
  ) => {
    setExercises((prev) =>
      prev.map((row) => (row.rowKey === rowKey ? { ...row, [field]: value } : row)),
    );
  };

  const removeExercise = (rowKey: string) => {
    setExercises((prev) => prev.filter((row) => row.rowKey !== rowKey));
    setPlaybackByRow((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const addExercise = () => {
    setExercises((prev) => [...prev, emptyExercise()]);
  };

  const clearPlayback = (rowKey: string) => {
    setPlaybackByRow((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const attachExistingVideo = (rowKey: string, videoId: string) => {
    setExercises((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
              ...row,
              videoId,
              videoSource: 'library',
              uploadState: 'idle',
              uploadError: undefined,
              uploadToken: undefined,
              notice: undefined,
            }
          : row,
      ),
    );
    clearPlayback(rowKey);
  };

  const clearVideo = (rowKey: string) => {
    setExercises((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
              ...row,
              videoId: null,
              videoSource: 'none',
              uploadState: 'idle',
              uploadError: undefined,
              uploadToken: undefined,
              notice: undefined,
            }
          : row,
      ),
    );
    clearPlayback(rowKey);
  };

  const detachVideo = (videoId: string) => {
    const rowKeys = exercisesRef.current
      .filter((row) => row.videoId === videoId)
      .map((row) => row.rowKey);
    setExercises((prev) =>
      prev.map((row) =>
        row.videoId === videoId
          ? {
              ...row,
              videoId: null,
              videoSource: 'none',
              uploadState: 'idle',
              uploadError: undefined,
              uploadToken: undefined,
              notice: undefined,
            }
          : row,
      ),
    );
    setPlaybackByRow((prev) => {
      if (rowKeys.every((rowKey) => !prev[rowKey])) return prev;
      const next = { ...prev };
      for (const rowKey of rowKeys) delete next[rowKey];
      return next;
    });
  };

  const confirmDeleteVideo = async () => {
    if (!pendingVideoDelete) return;
    setDeletingVideo(true);
    setError('');
    try {
      await deleteVideo(pendingVideoDelete).unwrap();
      detachVideo(pendingVideoDelete);
      setPendingVideoDelete(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось удалить ролик'));
      setPendingVideoDelete(null);
    } finally {
      setDeletingVideo(false);
    }
  };

  const leaveForVideoLimit = (rows: ExerciseRow[]) => {
    if (!clientId) return;
    const key = planFormDraftStorageKey(clientId, planId);
    savePlanFormDraftForVideoLimit(key, {
      clientId,
      name: nameRef.current,
      exercises: rows,
    });
    navigate('/videos', {
      state: {
        from: `${location.pathname}${location.search}`,
        planFormDraftKey: key,
      },
    });
  };

  const beginCustomUpload = async (rowKey: string, file: File) => {
    const token = crypto.randomUUID();
    let duration: number;
    try {
      duration = await readDurationSec(file);
    } catch {
      setExercises((prev) =>
        prev.map((row) =>
          row.rowKey === rowKey
            ? {
                ...row,
                uploadState: 'error',
                uploadError: t('Не удалось сжать ролик'),
                uploadToken: undefined,
                notice: undefined,
              }
            : row,
        ),
      );
      return;
    }
    if (duration > 90) {
      setExercises((prev) =>
        prev.map((row) =>
          row.rowKey === rowKey
            ? {
                ...row,
                uploadState: 'error',
                uploadError: t('Ролик длиннее 90 секунд'),
                uploadToken: undefined,
                notice: undefined,
              }
            : row,
        ),
      );
      return;
    }

    setExercises((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? {
              ...row,
              uploadState: 'uploading',
              uploadError: undefined,
              notice: undefined,
              uploadToken: token,
            }
          : row,
      ),
    );
    clearPlayback(rowKey);

    try {
      const exerciseName =
        exercisesRef.current.find((row) => row.rowKey === rowKey)?.name ?? '';
      const result = await uploadExerciseVideo(file, { name: exerciseName });
      setExercises((prev) =>
        prev.map((row) => {
          if (row.rowKey !== rowKey || row.uploadToken !== token) return row;
          return {
            ...row,
            videoId: result.videoId,
            videoSource: 'custom',
            uploadState: 'idle',
            uploadError: undefined,
            uploadToken: undefined,
            notice: result.muted ? t('Ролик загружен без звука') : undefined,
          };
        }),
      );
    } catch (err) {
      let nextRows = exercisesRef.current;
      setExercises((prev) => {
        nextRows = prev.map((row) => {
          if (row.rowKey !== rowKey || row.uploadToken !== token) return row;
          return {
            ...row,
            uploadState: 'error',
            uploadError: uploadErrorText(err, t),
            uploadToken: undefined,
          };
        });
        return nextRows;
      });
      if (isUploadLimitError(err)) {
        leaveForVideoLimit(nextRows);
      }
    }
  };

  const watchTechnique = async (rowKey: string, videoId: string) => {
    setPlaybackByRow((prev) => ({ ...prev, [rowKey]: { loading: true } }));
    try {
      const result = await fetchPlayback(videoId).unwrap();
      setPlaybackByRow((prev) => ({ ...prev, [rowKey]: { href: result.href } }));
    } catch {
      setPlaybackByRow((prev) => ({
        ...prev,
        [rowKey]: { error: t('Ролик временно недоступен') },
      }));
    }
  };

  const handleGenerate = async () => {
    if (!clientId || !goal.trim() || linkingVideos) return;

    if (hasFormContent()) {
      const confirmed = window.confirm(
        t('Текущие данные формы будут заменены. Продолжить?'),
      );
      if (!confirmed) return;
    }

    setError('');
    setMatchChoices(null);
    setLinkingVideos(true);
    try {
      const result = await generatePlan({ clientId, goal: goal.trim() }).unwrap();
      const rows = (result.exercises ?? []).map((ex) => toRow(ex));
      setName(result.name || '');
      setExercises(rows);
      setPlaybackByRow({});

      const pending = rows.filter((row) => !row.videoId);
      if (pending.length === 0) return;

      try {
        const matches = await matchMovements({
          names: pending.map((row) => row.name),
        }).unwrap();
        setMatchChoices(
          pending.map((row, index) => {
            const movement = matches[index]?.movement ?? null;
            return {
              rowKey: row.rowKey,
              name: row.name,
              movement,
              checked: movement !== null,
            };
          }),
        );
      } catch (err) {
        setError(getApiErrorMessage(err, 'Произошла ошибка при обращении к серверу'));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'ИИ сейчас недоступен.'));
    } finally {
      setLinkingVideos(false);
    }
  };

  const toggleMatchChoice = (rowKey: string) => {
    setMatchChoices((prev) =>
      prev?.map((choice) =>
        choice.rowKey === rowKey && choice.movement
          ? { ...choice, checked: !choice.checked }
          : choice,
      ) ?? null,
    );
  };

  const applyMatchedVideos = () => {
    if (!matchChoices) return;
    const chosen = new Map<string, NonNullable<MatchChoice['movement']>>();
    for (const choice of matchChoices) {
      if (choice.checked && choice.movement) chosen.set(choice.rowKey, choice.movement);
    }
    setExercises((prev) =>
      prev.map((row) => {
        if (row.videoId) return row;
        const movement = chosen.get(row.rowKey);
        if (!movement) return row;
        const videoId = movement.currentVideoId;
        return {
          ...row,
          movementId: movement.id,
          videoId,
          videoSource: videoId ? 'library' : 'none',
        };
      }),
    );
    setMatchChoices(null);
  };

  handleSaveRef.current = async (rows: ExerciseRow[]) => {
    if (persistLock.current || !clientId || !name.trim()) return;
    persistLock.current = true;
    const validExercises = toPayload(rows);
    setError('');
    try {
      if (isEdit && planId) {
        await updateTrainingPlan({
          id: planId,
          data: { name: name.trim(), exercises: validExercises },
        }).unwrap();
      } else {
        const created = await createTrainingPlan({
          clientId,
          name: name.trim(),
          exercises: validExercises,
        }).unwrap();

        if (returnContext?.returnTo) {
          saveBookingFormDraft({
            ...returnContext.returnState,
            clientId,
            trainingPlanId: created.id,
          });
          navigate(-1);
          return;
        }

        returnToPlans();
        return;
      }
      close();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось сохранить план'));
    } finally {
      persistLock.current = false;
    }
  };

  useEffect(() => {
    if (!saveAfterUpload || savingAfterRef.current) return;
    if (exercises.some((row) => row.uploadState === 'uploading')) return;
    savingAfterRef.current = true;
    setSaveAfterUpload(false);
    void handleSaveRef.current(exercises).finally(() => {
      savingAfterRef.current = false;
    });
  }, [saveAfterUpload, exercises]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !name.trim()) return;
    if (exercises.some((row) => row.uploadState === 'uploading')) {
      setSavePrompt(true);
      return;
    }
    void handleSaveRef.current(exercises);
  };

  const saveWithoutUnfinished = () => {
    setSavePrompt(false);
    setSaveAfterUpload(false);
    void handleSaveRef.current(exercises);
  };

  const waitForUploads = () => {
    setSavePrompt(false);
    setSaveAfterUpload(true);
  };

  const saving = isCreating || isUpdating;
  const planDetailLoading = isEdit && isLoading && !plan;
  const pageTitle = isEdit ? t('Редактировать план') : t('Новый план');

  const sharePlanAction = (
    <ShareButton
      getText={() => formatTrainingPlanText(name, exercises)}
      title={name.trim() || pageTitle}
      ariaLabel={t('Поделиться планом')}
      className="btn-ghost w-10 h-10 shrink-0 surface-solid !p-0 md:w-11 md:h-11"
      iconSize={20}
      onError={(err) =>
        setError(getApiErrorMessage(err, 'Не удалось поделиться планом'))
      }
    />
  );

  if (!clientId) {
    return null;
  }

  if (planDetailLoading) {
    return (
      <div className="page-container max-w-[800px] pb-20 min-w-0">
        <ScreenHeader title={pageTitle} subtitle={client?.name} onBack={close} />
        <div className="flex justify-center py-16">
          <div className="spinner h-10 w-10" />
        </div>
      </div>
    );
  }

  const videoRow = exercises.find((row) => row.rowKey === videoRowKey) ?? null;

  if (isEdit && planError && !plan) {
    return (
      <div className="page-container max-w-[800px] pb-20 min-w-0">
        <ScreenHeader title={pageTitle} subtitle={client?.name} onBack={close} />
        <ErrorAlert message={error || getApiErrorMessage(planError, 'Не удалось загрузить план')} />
        <button type="button" onClick={close} className="btn-secondary w-full mt-4">
          {t('Назад')}
        </button>
      </div>
    );
  }

  return (
    <div className="page-container max-w-[800px] pb-20 min-w-0">
      <ScreenHeader
        title={pageTitle}
        subtitle={client?.name}
        onBack={close}
        actions={sharePlanAction}
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <ErrorAlert message={error} onDismiss={() => setError('')} />}

        {isArchivedPlan && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {t('План в архиве. Восстановите его на странице клиента, чтобы снова назначать на тренировки.')}
          </div>
        )}

        {!isEdit && (
          <div className="card p-4 space-y-3 bg-primary-soft/50">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-primary shrink-0" />
              <span className="micro-label text-primary">
                {t('Генерация с ИИ')}
              </span>
            </div>
            <div className="space-y-1.5">
              <label className="section-label ml-1">
                {t('Цель клиента')}
              </label>
              <textarea
                className="input-field min-h-[80px] resize-y"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={t('Например: похудение, набор массы, подготовка к марафону')}
              />
            </div>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || linkingVideos || !goal.trim()}
              className="btn-primary w-full min-h-12"
            >
              {isGenerating || linkingVideos ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('Генерация...')}
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  {t('Сгенерировать план')}
                </>
              )}
            </button>
          </div>
        )}

        {!isEdit && copySourcePlans.length > 0 && (
          <div className="card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Copy size={16} className="text-slate-500 shrink-0" />
              <span className="section-label">
                {t('Скопировать из плана')}
              </span>
            </div>
            <select
              className="input-field font-medium appearance-none cursor-pointer"
              value={copyFromPlanId}
              disabled={copyingPlan}
              onChange={(e) => {
                const value = e.target.value;
                setCopyFromPlanId(value);
                void handleCopyFromPlan(value);
              }}
            >
              <option value="">{t('Выберите план')}</option>
              {copySourcePlans.map((plan) => {
                const dateLabel = new Date(plan.createdAt).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                });
                const exerciseLabel =
                  plan.exerciseCount !== undefined
                    ? t('{{count}} упр.', { count: plan.exerciseCount })
                    : '';
                const suffix = [dateLabel, exerciseLabel].filter(Boolean).join(' · ');
                return (
                  <option key={plan.id} value={plan.id}>
                    {suffix ? `${plan.name} — ${suffix}` : plan.name}
                  </option>
                );
              })}
            </select>
            {copyingPlan && (
              <p className="text-xs text-slate-500 flex items-center gap-1.5 ml-1">
                <Loader2 size={14} className="animate-spin" />
                {t('Копирование...')}
              </p>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="section-label ml-1">
            {t('Название плана')}
          </label>
          <AutoHeightTextarea
            className="input-field font-medium"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-3">
          <label className="section-label ml-1 block">
            {t('Упражнения')}
          </label>

          <div className="space-y-3">
            {exercises.map((exercise, index) => {
              const playback = playbackByRow[exercise.rowKey];
              return (
              <div
                key={exercise.rowKey}
                className="card p-3 sm:p-4 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <span className="section-label shrink-0 pt-3.5">
                    #{index + 1}
                  </span>
                  <AutoHeightTextarea
                    className="input-field flex-1 min-w-0 py-2.5"
                    placeholder={t('Название упражнения')}
                    value={exercise.name}
                    onChange={(e) => updateExercise(exercise.rowKey, 'name', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeExercise(exercise.rowKey)}
                    className="btn-ghost shrink-0 min-h-11 min-w-11 text-slate-400 hover:text-red-500"
                    aria-label={t('Удалить')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="section-label mb-1 block">
                      {t('Подходы')}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="input-field py-2.5 text-center"
                      placeholder="3"
                      value={exercise.sets}
                      onChange={(e) => updateExercise(exercise.rowKey, 'sets', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="section-label mb-1 block">
                      {t('Повторы')}
                    </label>
                    <input
                      type="text"
                      className="input-field py-2.5 text-center"
                      placeholder="12"
                      value={exercise.reps}
                      onChange={(e) => updateExercise(exercise.rowKey, 'reps', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="section-label mb-1 block">
                      {t('Отдых')}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="input-field py-2.5 text-center"
                      placeholder="60"
                      value={exercise.restSeconds}
                      onChange={(e) => updateExercise(exercise.rowKey, 'restSeconds', e.target.value)}
                    />
                  </div>
                </div>

                <AutoHeightTextarea
                  className="input-field py-2.5"
                  placeholder={t('Заметки (необязательно)')}
                  value={exercise.notes}
                  onChange={(e) => updateExercise(exercise.rowKey, 'notes', e.target.value)}
                />

                {exercise.uploadState === 'uploading' ? (
                  <div
                    className="flex min-w-0 items-start gap-2.5 rounded-2xl border border-app-border bg-white px-4 py-3 text-sm leading-relaxed text-slate-600"
                    role="status"
                  >
                    <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-slate-400" />
                    <span className="min-w-0 break-words">
                      {t('Загружаем ролик. Не закрывайте страницу')}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-stretch gap-2">
                    <button
                      type="button"
                      onClick={() => setVideoRowKey(exercise.rowKey)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-2xl border border-app-border bg-white px-4 py-3 text-left text-base font-normal text-slate-900 md:text-[15px]"
                    >
                      <span className="min-w-0 break-words">{videoSourceLabel(exercise.videoSource, t)}</span>
                    </button>
                    {exercise.videoId && (
                      <button
                        type="button"
                        onClick={() => setPendingVideoDelete(exercise.videoId!)}
                        className="btn-ghost shrink-0 min-h-11 min-w-11 text-slate-400 hover:text-red-500"
                        aria-label={t('Удалить ролик')}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                )}
                {exercise.uploadError && (
                  <p className="min-w-0 break-words rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-medium leading-relaxed text-red-600">
                    {exercise.uploadError}
                  </p>
                )}
                {exercise.notice && (
                  <p className="text-sm text-slate-500">{exercise.notice}</p>
                )}
                {exercise.videoId && exercise.uploadState !== 'uploading' && (
                  <button
                    type="button"
                    className="btn-secondary w-full min-h-12"
                    onClick={() => void watchTechnique(exercise.rowKey, exercise.videoId!)}
                  >
                    {playback?.loading && <Loader2 size={16} className="animate-spin" />}
                    {t('Смотреть технику')}
                  </button>
                )}
                {playback?.error && (
                  <p className="min-w-0 break-words rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-medium leading-relaxed text-red-600">
                    {playback.error}
                  </p>
                )}
                {playback?.href && (
                  <video controls src={playback.href} className="w-full rounded-2xl bg-black" />
                )}
              </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addExercise}
            className="btn-secondary w-full min-h-12"
          >
            <Plus size={18} />
            {t('Добавить упражнение')}
          </button>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <button type="button" onClick={close} className="btn-secondary flex-1 min-h-12">
            {t('Отмена')}
          </button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 sm:flex-[2] min-h-12">
            {saving ? t('Сохранение...') : t('Сохранить')}
          </button>
        </div>
      </form>

      {videoRow && (
        <ExerciseVideoSheet
          name={videoRow.name}
          hasVideo={videoRow.videoId != null}
          onClose={() => setVideoRowKey(null)}
          onRemove={() => clearVideo(videoRow.rowKey)}
          onDelete={() => {
            if (!videoRow.videoId) return;
            setPendingVideoDelete(videoRow.videoId);
            setVideoRowKey(null);
          }}
          onPickFile={(file) => {
            const rowKey = videoRow.rowKey;
            setVideoRowKey(null);
            void beginCustomUpload(rowKey, file);
          }}
          onPickExisting={(videoId) => {
            const rowKey = videoRow.rowKey;
            setVideoRowKey(null);
            attachExistingVideo(rowKey, videoId);
          }}
        />
      )}

      {matchChoices && (
        <MatchVideosSheet
          choices={matchChoices}
          onClose={() => setMatchChoices(null)}
          onToggle={toggleMatchChoice}
          onApply={applyMatchedVideos}
        />
      )}

      {pendingVideoDelete && (
        <Modal onClose={() => setPendingVideoDelete(null)} panelClassName="sm:max-w-md">
          <div className="modal-header flex justify-between items-start gap-4">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
              {t('Удалить ролик')}
            </h2>
            <button
              type="button"
              onClick={() => setPendingVideoDelete(null)}
              className="btn-ghost shrink-0 -mr-1 -mt-1"
              aria-label={t('Закрыть')}
            >
              <X size={22} />
            </button>
          </div>
          <p className="text-sm text-slate-600">
            {t('Вы уверены, что хотите удалить этот ролик?')}
          </p>
          <div className="modal-actions flex flex-col gap-3 mt-6">
            <button
              type="button"
              onClick={() => void confirmDeleteVideo()}
              disabled={deletingVideo}
              className="btn-primary w-full min-h-12"
            >
              {deletingVideo && <Loader2 size={16} className="animate-spin" />}
              {t('Удалить')}
            </button>
            <button
              type="button"
              onClick={() => setPendingVideoDelete(null)}
              className="btn-secondary w-full min-h-12"
            >
              {t('Отмена')}
            </button>
          </div>
        </Modal>
      )}

      {savePrompt && (
        <Modal onClose={() => setSavePrompt(false)} panelClassName="sm:max-w-md">
          <div className="modal-header flex justify-between items-start gap-4">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
              {t('Сохранить')}
            </h2>
            <button
              type="button"
              onClick={() => setSavePrompt(false)}
              className="btn-ghost shrink-0 -mr-1 -mt-1"
              aria-label={t('Закрыть')}
            >
              <X size={22} />
            </button>
          </div>
          <p className="text-sm text-slate-600">
            {t('Дождитесь окончания загрузки или сохраните без незавершённых роликов')}
          </p>
          <div className="modal-actions flex flex-col gap-3 mt-6">
            <button type="button" onClick={waitForUploads} className="btn-secondary w-full min-h-12">
              {t('Ждать')}
            </button>
            <button type="button" onClick={saveWithoutUnfinished} className="btn-primary w-full min-h-12">
              {t('Сохранить без незавершённых роликов')}
            </button>
            <button type="button" onClick={() => setSavePrompt(false)} className="btn-secondary w-full min-h-12">
              {t('Отмена')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TrainingPlanFormRoute;

function MatchVideosSheet({
  choices,
  onClose,
  onToggle,
  onApply,
}: {
  choices: MatchChoice[];
  onClose: () => void;
  onToggle: (rowKey: string) => void;
  onApply: () => void;
}) {
  const { t } = useTranslation();
  const matched = choices.filter((choice) => choice.movement).length;

  return (
    <Modal onClose={onClose} panelClassName="sm:max-w-md">
      <div className="modal-header flex justify-between items-start gap-4">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight pr-2">
          {t('Подставить ролики')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost shrink-0 -mr-1 -mt-1"
          aria-label={t('Закрыть')}
        >
          <X size={22} />
        </button>
      </div>
      <p className="text-sm text-slate-600">{t('Совпало {{count}}', { count: matched })}</p>
      <ul className="space-y-2 mt-4">
        {choices.map((choice) => (
          <li key={choice.rowKey}>
            {choice.movement ? (
              <label className="flex items-start gap-3 rounded-2xl border border-app-border bg-white px-4 py-3">
                <input
                  type="checkbox"
                  className="size-5 mt-0.5 shrink-0 rounded-md border-slate-300 accent-primary"
                  checked={choice.checked}
                  onChange={() => onToggle(choice.rowKey)}
                />
                <span className="block font-medium text-slate-800 break-words">{choice.name}</span>
              </label>
            ) : (
              <div className="rounded-2xl border border-app-border bg-white px-4 py-3">
                <span className="block font-medium text-slate-800 break-words">{choice.name}</span>
                <span className="block text-xs text-slate-500 mt-0.5">{t('Не найдено')}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="modal-actions mt-6">
        <button type="button" onClick={onApply} className="btn-primary w-full min-h-12">
          {t('Подставить')}
        </button>
      </div>
    </Modal>
  );
}
